import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/lib/fa/';

import { sections } from '../../../lib/playerPreferenceMeta';
import { anchorTo, bindPopover } from '../../../lib/popoverAnchor';

/**
 * Player editor sidebar nav. Tab list is derived from the v4
 * `sections` export (in v3 2.7 sortOrder). Player-local copy of the
 * shared `embed/lib/Navigation` -- left untouched because listing
 * still consumes it on its v3 path.
 *
 * COLLAPSED MODE
 * The Theme Builder takes the editor over (see `PlayerPreferenceEditor`),
 * and the ~24rem this nav occupies is worth more to the control rail and the
 * player preview than to a list of ten links you are not currently using. So
 * it collapses to a strip carrying one button, and the full list returns as a
 * flyout over the rail.
 *
 * A flyout rather than a strip of ten icons: nine of these sections have no
 * settled iconography, and inventing it here would mean nine guesses the user
 * has to learn. The one thing the strip must do is get you out, and a labelled
 * list does that without a legend.
 */
const FLYOUT_W = 220;
const FLYOUT_H = 400;

export default function Navigation ({ activeTabKey, onSelect, collapsed = false }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [flyoutOpen, setFlyoutOpen] = useState(false);
    const [flyoutAnchor, setFlyoutAnchor] = useState(null);
    const active = sections.find(s => s.key === activeTabKey);
    const flyoutRef = useRef(null);
    const stripRef = useRef(null);

    const toggleMobile = useCallback((ev) => {
        if (ev) ev.preventDefault();
        setMobileOpen(prev => !prev);
    }, []);

    const handleSelect = useCallback((sectionKey, ev) => {
        if (ev) ev.preventDefault();
        onSelect(sectionKey);
        setMobileOpen(false);
        setFlyoutOpen(false);
    }, [onSelect]);

    const place = useCallback(() => {
        setFlyoutAnchor(anchorTo(stripRef.current, FLYOUT_W, FLYOUT_H, 4));
    }, []);

    // Dismiss on outside click and on Escape. An overlay that can only be
    // closed by picking something from it is a trap -- opening it to check
    // where you are is a legitimate reason to open it.
    useEffect(() => {
        if (!flyoutOpen) return undefined;
        place();
        return bindPopover({
            onDismiss: () => setFlyoutOpen(false),
            onReplace: place,
            ignore: [stripRef, flyoutRef]
        });
    }, [flyoutOpen, place]);

    if (collapsed) {
        return (
            <nav className="responsive-nav responsive-nav--collapsed">
                <button
                    type="button"
                    ref={stripRef}
                    className="responsive-nav__strip-toggle"
                    aria-expanded={flyoutOpen}
                    aria-haspopup="true"
                    onClick={() => setFlyoutOpen(prev => !prev)}
                    title="All preference settings"
                >
                    <i className="fa fa-bars" aria-hidden="true"/>
                    <span className="sr-only">All preference settings</span>
                </button>

                {/* Portalled to <body>, like the colour picker and the help
                    tips. Not for clipping -- nothing here clips it -- but for
                    INHERITANCE. Rendered in place it sat inside
                    `#player-preference-editor nav`, which styles list items
                    for the full-height sidebar column: a 50px minimum row, a
                    grey fill and a hover a shade darker. This menu's own <a>
                    is shorter than 50px, so hovering lit the link in one
                    colour while the row behind it lit in another, and the
                    mismatch showed as a dark band across the bottom of the
                    row. Out here it inherits none of it, and a future change
                    to the sidebar cannot reintroduce the same collision. */}
                {flyoutOpen && flyoutAnchor ? createPortal(
                    <ul
                        className="theme-nav-flyout"
                        ref={flyoutRef}
                        style={{ left: flyoutAnchor.left + 'px', top: flyoutAnchor.top + 'px' }}
                    >
                        {sections.map((s) => (
                            <li key={s.key}
                                className={s.key === activeTabKey ? 'is-active' : ''}>
                                <a href="#" onClick={(ev) => handleSelect(s.key, ev)}>
                                    {s.displayName}
                                </a>
                            </li>
                        ))}
                    </ul>,
                    document.body
                ) : null}
            </nav>
        );
    }

    return (
        <nav className="responsive-nav media__lessThan--sm">
            <ul className={mobileOpen ? 'mobile-nav__open' : ''}>

                <li className="mobile-nav-header">
                    <a href="#" onClick={toggleMobile}>
                        Current Tab: <strong>{active ? active.displayName : ''}</strong>
                        <span className="icon">
                            {mobileOpen ?
                                <FaMinusCircle size={25}/> :
                                <FaPlusCircle size={25}/>
                            }
                        </span>
                    </a>
                </li>

                {sections.map((s) => (
                    <li key={s.key}
                        className={s.key === activeTabKey ? 'active' : ''}>
                        <a href="#" onClick={(ev) => handleSelect(s.key, ev)}>
                            {s.displayName}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
